import { useEffect, useState, useRef, useCallback } from "react";


const blobCache = new Map(); 
const pendingFetches = new Map();

async function defaultRequestApiFetch(documentId, endPointBuilder = null) {
    let requestAPI;
    try {
        requestAPI = require("../../helpers/requestHelpers").default || require("../../helpers/requestHelpers");
    } catch (e) {
        throw new Error(
            "Nenhuma função getFileBlob fornecida e import de requestHelpers falhou. Passe getFileBlob em options."
        );
    }

    let endPoint;
    if (typeof endPointBuilder === "function") {
        endPoint = endPointBuilder(documentId);
    } else {
        endPoint = `/documents/${encodeURIComponent(documentId)}/file`;
    }

    const resp = await requestAPI({
        verb: "GET",
        endPoint,
        data: null,
        responseType: "blob",
    });

    if (resp && resp instanceof Blob) return resp;
    if (resp && resp.data && resp.data instanceof Blob) return resp.data;

    throw new Error("Resposta do requestAPI não contém Blob. Ajuste getFileBlob ou o defaultRequestApiFetch.");
}

function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    a.download = filename || "documento.bin";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
}

async function fetchAndCacheBlob(documentId, getFileBlob, endPointBuilder = null) {
    if (!documentId && documentId !== 0) throw new Error("documentId is required");
    if (blobCache.has(documentId)) return blobCache.get(documentId);
    if (pendingFetches.has(documentId)) return pendingFetches.get(documentId);

    const p = (async () => {
        try {
            let blob;
            if (typeof getFileBlob === "function") {
                blob = await getFileBlob(documentId);
            } else {
                blob = await defaultRequestApiFetch(documentId, endPointBuilder);
            }

            if (!(blob instanceof Blob)) {
                if (blob && blob.data && blob.data instanceof ArrayBuffer) {
                    blob = new Blob([blob.data]);
                } else if (blob instanceof ArrayBuffer) {
                    blob = new Blob([blob]);
                } else {
                    throw new Error("Resposta não é um Blob/ArrayBuffer. Ajuste getFileBlob.");
                }
            }

            blobCache.set(documentId, blob);
            pendingFetches.delete(documentId);
            return blob;
        } catch (err) {
            pendingFetches.delete(documentId);
            throw err;
        }
    })();

    pendingFetches.set(documentId, p);
    try {
        return await p;
    } catch (e) {
        pendingFetches.delete(documentId);
        throw e;
    }
}

// Hook
export function useImageMemoryCacheForDocument(documentId, options = {}) {
    const { autoFetch = true, getFileBlob = null, endPointBuilder = null } = options;
    const [objectUrl, setObjectUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const currentObjectUrlRef = useRef(null);

    const doFetch = useCallback(
        async (opts = {}) => {
            if (!documentId && documentId !== 0) {
                setError(new Error("documentId is required"));
                return;
            }

            setLoading(true);
            setError(null);
            try {
                const blob = await fetchAndCacheBlob(documentId, getFileBlob, endPointBuilder);
                const obj = URL.createObjectURL(blob);
                currentObjectUrlRef.current = obj;
                setObjectUrl(obj);
                return obj;
            } catch (err) {
                setError(err instanceof Error ? err : new Error(String(err)));
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [documentId, getFileBlob, endPointBuilder]
    );

    useEffect(() => {
        if (autoFetch && (documentId || documentId === 0)) {
            doFetch().catch(() => {});
        } else if (documentId == null) {
            setObjectUrl(null);
            setError(null);
            setLoading(false);
        }
        return () => {};
    }, [documentId, autoFetch, doFetch]);

    const download = useCallback(
        (filename) => {
            if (!documentId && documentId !== 0) throw new Error("documentId is required to download");
            const blob = blobCache.get(documentId);
            if (!blob) throw new Error("File not cached yet. Call fetch() or use autoFetch first.");
            downloadBlob(blob, filename || `document-${documentId}.bin`);
        },
        [documentId]
    );

    const clear = useCallback(() => {
        if (documentId == null) return;
        blobCache.delete(documentId);
        setObjectUrl(null);
        setError(null);
    }, [documentId]);

    return {
        objectUrl,
        loading,
        error,
        fetch: doFetch,
        download,
        clear,
    };
}